import { useId, useState, type ReactNode } from 'react';
import { DialogLayer } from '../ui/Dialog';
import { TimelineList } from './Timeline';
import type { TimelinePoint } from './timeline';
import { dayShortText } from './types';

/** One question in the full „Historia”, as the lists send it (rule 7a applied by the server). */
export type HistoryItem = {
  id: string;
  eventId?: string;
  eventName?: string;
  eventDeleted?: true;
  personName?: string | null;
  sentAt?: string;
  active?: boolean;
  days?: Array<{ day: string }>;
  timeline?: TimelinePoint[];
};

type Period = 'all' | '30' | '90' | '365';
const PERIODS: Array<[Period, string]> = [
  ['all', 'Cały czas'],
  ['30', 'Ostatnie 30 dni'],
  ['90', 'Ostatnie 3 miesiące'],
  ['365', 'Ostatni rok'],
];

/**
 * How a group is named: the event; the deleted events together („wydarzenie usunięte”) and the events whose name is
 * hidden together (review M4); a question about days on its own („Zapytanie o dni” with its days).
 */
function groupOf(q: HistoryItem): { key: string; label: string } {
  if (q.eventId) return { key: `e:${q.eventId}`, label: q.eventName ?? 'Wydarzenie' };
  if (q.eventDeleted) return { key: 'deleted', label: q.eventName ?? 'wydarzenie usunięte' };
  if (q.eventName) return { key: `n:${q.eventName}`, label: q.eventName };
  const days = (q.days ?? []).map((d) => d.day).sort();
  const range = days.length === 0 ? '' : days.length === 1 ? dayShortText(days[0]!) : `${dayShortText(days[0]!)} – ${dayShortText(days.at(-1)!)}`;
  return { key: `q:${q.id}`, label: range ? `Zapytanie o dni (${range})` : 'Zapytanie o dni' };
}
/** When the question last moved: its newest point — or, that point's time hidden (rule 7a), when it was sent (review M4). */
const newest = (q: HistoryItem) => q.timeline?.at(-1)?.at ?? q.sentAt ?? '';

/**
 * Owner 2026-10-30 21:49 („pełna historia niech się zapisuje w historii (jakiś guziczek)”): every question, the inactive
 * ones too (past, stale, replaced, a deleted event, removed from the crew), grouped by event (a question about days on
 * its own), the newest first, each with its whole correspondence — read-only. Filters: the event and the period.
 * `mine` („Mój kalendarz”): „Twoja odpowiedź”, „Ty”.
 */
export function QuestionHistory({ items, mine = false, now = new Date() }: { items: readonly HistoryItem[]; mine?: boolean; now?: Date }) {
  const id = useId();
  const [event, setEvent] = useState('');
  const [period, setPeriod] = useState<Period>('all');
  const since = period === 'all' ? '' : new Date(now.getTime() - Number(period) * 86_400_000).toISOString();
  const groups = new Map<string, { label: string; items: HistoryItem[] }>();
  for (const q of [...items].sort((a, b) => newest(b).localeCompare(newest(a)))) {
    const g = groupOf(q);
    const entry = groups.get(g.key) ?? { label: g.label, items: [] };
    entry.items.push(q);
    groups.set(g.key, entry);
  }
  const all = [...groups.entries()];
  const shown = all.filter(([key, g]) => (event === '' || key === event) && (since === '' || g.items.some((q) => newest(q) >= since)));
  return (
    <div className="qh" data-feature-id="requests.answers">
      <div className="qh-filters">
        <label htmlFor={`${id}-e`}>Wydarzenie</label>
        <select id={`${id}-e`} value={event} onChange={(e) => setEvent(e.target.value)}>
          <option value="">Wszystkie</option>
          {all.map(([key, g]) => (
            <option key={key} value={key}>
              {g.label}
            </option>
          ))}
        </select>
        <label htmlFor={`${id}-p`}>Okres</label>
        <select id={`${id}-p`} value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
          {PERIODS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {shown.length === 0 ? (
        <p className="muted">Brak zapytań w tym okresie.</p>
      ) : (
        shown.map(([key, g]) => (
          <section key={key} className="qh-group" aria-label={g.label}>
            <h3>{g.label}</h3>
            {g.items.map((q) => (
              <div key={q.id} className="qh-item">
                <div className="qh-head">
                  {q.personName && <span className="qh-who">{q.personName}</span>}
                  {q.active === false && <span className="qh-tag">nieaktywne</span>}
                </div>
                {q.timeline && q.timeline.length > 0 ? <TimelineList points={q.timeline} mine={mine} /> : <p className="muted">Bez historii (brak prawa do odpowiedzi).</p>}
              </div>
            ))}
          </section>
        ))
      )}
    </div>
  );
}

/** „Historia” as a dialog over the list: `children` above the history (e.g. the month it reads), Esc / „Zamknij” close it. */
export function HistoryDialog({ onClose, loading, error, children }: { onClose: () => void; loading: boolean; error: string | null; children: ReactNode }) {
  const title = useId();
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel qh-dialog" role="dialog" aria-modal="true" aria-labelledby={title}>
        <DialogLayer onClose={onClose} />
        <div className="qh-dialog-head">
          <h2 id={title}>Historia zapytań</h2>
          <button type="button" className="ep-close" aria-label="Zamknij" onClick={onClose}>
            ×
          </button>
        </div>
        <p className="muted">Wszystkie zapytania i odpowiedzi, także te, których nie ma już na liście. Tylko do przeczytania — nic tu się nie zmienia.</p>
        {loading && (
          <p className="muted" role="status">
            Wczytywanie…
          </p>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}
